import type { MessageContent } from './protocol/index.js'

export interface OutboxItem {
  clientMsgId: string
  content:     MessageContent
  ts:          number
}

const MAX_ITEMS = 200          // cap so a long outage can't grow storage unboundedly
const MAX_AGE_MS = 7 * 86_400_000  // drop anything older than 7 days on load

/** Persists not-yet-acknowledged outgoing messages to localStorage, keyed by
 *  guest token, so a page reload during a connectivity drop doesn't silently
 *  lose what the user typed (the "WhatsApp" guarantee: your message is queued
 *  until it's confirmed sent, even across app restarts). */
export class PersistentOutbox {
  private readonly key: string

  constructor(token: string) {
    this.key = `ocw_outbox_${token}`
  }

  /** All pending items, oldest first, with stale (>7d) entries dropped. */
  load(): OutboxItem[] {
    try {
      const raw = localStorage.getItem(this.key)
      if (!raw) return []
      const items = JSON.parse(raw) as OutboxItem[]
      const cutoff = Date.now() - MAX_AGE_MS
      const fresh = items.filter(i => i.ts >= cutoff)
      if (fresh.length !== items.length) this.save(fresh)
      return fresh
    } catch {
      return []
    }
  }

  add(item: OutboxItem): void {
    try {
      const items = this.load()
      items.push(item)
      // Evict oldest when full — matches the in-memory ConnectionManager outbox policy.
      this.save(items.length > MAX_ITEMS ? items.slice(items.length - MAX_ITEMS) : items)
    } catch { /* localStorage unavailable (private mode, quota) — best-effort only */ }
  }

  /** Remove an item once it's been acknowledged by the server. */
  remove(clientMsgId: string): void {
    try {
      const items = this.load().filter(i => i.clientMsgId !== clientMsgId)
      this.save(items)
    } catch { /* best-effort */ }
  }

  private save(items: OutboxItem[]): void {
    try { localStorage.setItem(this.key, JSON.stringify(items)) } catch { /* quota exceeded — drop silently */ }
  }
}
